'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ArrowRight, Check, CreditCard, Film, ImageIcon, Instagram, LoaderCircle, LockKeyhole, Music2, ShieldCheck, X, Youtube } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  BILLING_MODEL_OPTIONS,
  BILLING_PERIOD_OPTIONS,
  PRICE_BASIS_OPTIONS,
  PRICE_CURRENCY_OPTIONS,
  PRICING_TYPE_OPTIONS,
  parseStartingPrice,
  validateSubmissionPricingDetails,
} from '@/libs/submission-details';
import {
  uploadSubmissionScreenshot,
  validateScreenshotFile,
} from '@/libs/product-screenshot-upload';
import type { PlanCode, StripePlan } from '@/types/config';
import { cn } from '@/utils/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';

function createFormSchema(t: any) {
  return z.object({
    website: z.string().trim().min(1, t('validation.websiteRequired')),
    url: z.string().trim().url(t('validation.invalidUrl')),
    category: z.string().min(1, t('validation.categoryRequired')),
    pricingType: z.string().optional(),
    billingModel: z.string().optional(),
    billingPeriod: z.string().optional(),
    startingPrice: z.string().trim().max(24, t('validation.startingPriceTooLong')).refine(
      (value) => !value || parseStartingPrice(value) !== null,
      t('validation.startingPriceInvalid'),
    ),
    priceCurrency: z.string().optional(),
    priceBasis: z.string().optional(),
    priceUnit: z.string().trim().max(40, t('validation.billingUnitTooLong')).optional(),
    suggestedKeywords: z.string().trim().max(800, t('validation.keywordsTooLong')),
    productBrief: z.string().trim().max(2000, t('validation.briefTooLong')),
  }).superRefine((values, ctx) => {
    for (const issue of validateSubmissionPricingDetails(values)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t(`validation.${issue.field}`),
        path: [issue.field],
      });
    }
  });
}

type SubmitValues = z.infer<ReturnType<typeof createFormSchema>>;
type CheckoutStage = 'saving' | 'uploading' | 'checkout' | 'redirecting';

const PENDING_SUBMISSION_KEY = 'aitoolly.pendingSubmission';

function localPath(locale: string, path: string) {
  return locale === 'en' ? path : `/${locale}${path}`;
}

export default function SubmitForm({
  locale,
  plans,
  screenshotUploadEnabled = false,
}: {
  className?: string;
  locale: string;
  plans: StripePlan[];
  screenshotUploadEnabled?: boolean;
}) {
  const t = useTranslations('SubmitPageV2');
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClientComponentClient();
  const selectedPlan = plans.find((plan) => plan.planCode === searchParams?.get('plan')) || plans.find((plan) => plan.planCode === 'listing')!;
  const formSchema = useMemo(() => createFormSchema(t), [t]);
  const priceFormatter = useMemo(() => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
  }), [locale]);
  const checkoutStages = useMemo<Array<{ id: CheckoutStage; label: string }>>(() => [
    { id: 'saving', label: t('checkoutStages.saving.label') },
    { id: 'uploading', label: t('checkoutStages.uploading.label') },
    { id: 'checkout', label: t('checkoutStages.checkout.label') },
    { id: 'redirecting', label: t('checkoutStages.redirecting.label') },
  ], [t]);
  const checkoutStageCopy = useMemo<Record<CheckoutStage, { eyebrow: string; title: string; description: string }>>(() => ({
    saving: {
      eyebrow: t('checkoutStages.saving.eyebrow'),
      title: t('checkoutStages.saving.title'),
      description: t('checkoutStages.saving.description'),
    },
    uploading: {
      eyebrow: t('checkoutStages.uploading.eyebrow'),
      title: t('checkoutStages.uploading.title'),
      description: t('checkoutStages.uploading.description'),
    },
    checkout: {
      eyebrow: t('checkoutStages.checkout.eyebrow'),
      title: t('checkoutStages.checkout.title'),
      description: t('checkoutStages.checkout.description'),
    },
    redirecting: {
      eyebrow: t('checkoutStages.redirecting.eyebrow'),
      title: t('checkoutStages.redirecting.title'),
      description: t('checkoutStages.redirecting.description'),
    },
  }), [t]);
  const planCopy = (plan: StripePlan, field: 'name' | 'shortName' | 'description') => t(`plans.${plan.planCode}.${field}`);
  const planFeature = (plan: StripePlan, index: number) => t(`plans.${plan.planCode}.feature${index + 1}`);
  const formatPrice = (price: number) => priceFormatter.format(price);
  const [loading, setLoading] = useState(false);
  const [checkoutStage, setCheckoutStage] = useState<CheckoutStage>('saving');
  const [authChecking, setAuthChecking] = useState(false);
  const [loginRequired, setLoginRequired] = useState(false);
  const [categoryList, setCategoryList] = useState<any[]>([]);
  const [isCategoryLoading, setIsCategoryLoading] = useState(true);
  const [videoAgreement, setVideoAgreement] = useState(false);
  const [drValue, setDrValue] = useState<string | null>(null);
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState('');
  const [screenshotUploadError, setScreenshotUploadError] = useState('');
  const submitContentRef = useRef<HTMLDivElement>(null);
  const checkoutDialogRef = useRef<HTMLDivElement>(null);

  const form = useForm<SubmitValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      website: '',
      url: '',
      category: '',
      pricingType: '',
      billingModel: '',
      billingPeriod: '',
      startingPrice: '',
      priceCurrency: 'USD',
      priceBasis: '',
      priceUnit: '',
      suggestedKeywords: '',
      productBrief: '',
    },
  });
  const pricingType = form.watch('pricingType');
  const showPaidPricingFields = pricingType === 'free_trial'
    || pricingType === 'freemium'
    || pricingType === 'paid';
  const billingModel = form.watch('billingModel');
  const showBillingPeriod = showPaidPricingFields && billingModel === 'subscription';
  const showPriceBasis = showPaidPricingFields
    && ['usage_based', 'credits', 'other'].includes(billingModel || '');
  const priceBasis = form.watch('priceBasis');
  const showPriceUnit = showPriceBasis && priceBasis === 'per_unit';
  const requireStartingPrice = showPaidPricingFields
    && Boolean(billingModel)
    && billingModel !== 'contact_sales';

  const fetchCategories = useCallback(async () => {
    setIsCategoryLoading(true);
    const { data, error } = await supabase
      .from('category_intl')
      .select('id, category_name, category_id')
      .eq('locale', locale)
      .order('category_name');

    if (error) {
      toast.error(t('errors.categoriesLoad'));
    } else {
      setCategoryList(data || []);
    }
    setIsCategoryLoading(false);
  }, [locale, supabase, t]);

  useEffect(() => {
    fetchCategories();
    fetch('/api/config/dr')
      .then((response) => response.json())
      .then((data) => setDrValue(data.dr == null ? null : String(data.dr)))
      .catch(() => setDrValue(null));
  }, [fetchCategories]);

  useEffect(() => {
    try {
      const pending = JSON.parse(sessionStorage.getItem(PENDING_SUBMISSION_KEY) || 'null');
      if (pending?.planCode === selectedPlan.planCode && pending?.values) {
        form.reset(pending.values);
      }
    } catch {
      sessionStorage.removeItem(PENDING_SUBMISSION_KEY);
    }
  }, [form, selectedPlan.planCode]);

  useEffect(() => {
    if (!screenshotFile) {
      setScreenshotPreview('');
      return;
    }
    const previewUrl = URL.createObjectURL(screenshotFile);
    setScreenshotPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [screenshotFile]);

  useEffect(() => {
    if (!loading) return;

    const previousOverflow = document.body.style.overflow;
    const submitContent = submitContentRef.current;
    submitContent?.setAttribute('inert', '');
    submitContent?.setAttribute('aria-hidden', 'true');
    const focusFrame = window.requestAnimationFrame(() => checkoutDialogRef.current?.focus());
    document.body.style.overflow = 'hidden';

    return () => {
      window.cancelAnimationFrame(focusFrame);
      submitContent?.removeAttribute('inert');
      submitContent?.removeAttribute('aria-hidden');
      document.body.style.overflow = previousOverflow;
    };
  }, [loading]);

  const choosePlan = (planCode: PlanCode) => {
    setVideoAgreement(false);
    setLoginRequired(false);
    router.replace(`${localPath(locale, '/submit')}?plan=${planCode}&mode=purchase`, { scroll: false });
  };

  const getSignInDestination = () => {
    const returnTo = `${localPath(locale, '/submit')}?plan=${selectedPlan.planCode}&mode=purchase`;
    return `${localPath(locale, '/signin')}?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const promptForLogin = () => {
    sessionStorage.setItem(PENDING_SUBMISSION_KEY, JSON.stringify({
      planCode: selectedPlan.planCode,
      values: form.getValues(),
    }));
    setLoginRequired(true);
    toast.error(t('errors.signInRequired'));
  };

  const handleSubmitAttempt = async () => {
    if (loading || authChecking) return;
    setAuthChecking(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        promptForLogin();
        return;
      }
      setLoginRequired(false);
      await form.handleSubmit(handleContinueToPayment)();
    } catch {
      toast.error(t('errors.authCheck'));
    } finally {
      setAuthChecking(false);
    }
  };

  const handleContinueToPayment = async (values: SubmitValues) => {
    if (selectedPlan.includesVideo && !videoAgreement) {
      toast.error(t('errors.videoTerms'));
      return;
    }

    setCheckoutStage('saving');
    setLoading(true);
    let redirectStarted = false;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        promptForLogin();
        return;
      }

      const submitResponse = await fetch('/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, submissionLocale: 'en' }),
      });
      const submitData = await submitResponse.json();
      if (!submitResponse.ok || !submitData.submitId) {
        throw new Error(t('errors.submissionSave'));
      }

      if (screenshotFile) {
        setCheckoutStage('uploading');
        setScreenshotUploadError('');
        try {
          await uploadSubmissionScreenshot(Number(submitData.submitId), screenshotFile);
        } catch {
          const message = t('errors.screenshotProcess');
          setScreenshotUploadError(message);
          throw new Error(t('errors.screenshotRetry'));
        }
      }

      setCheckoutStage('checkout');
      const basePath = localPath(locale, '');
      const checkoutResponse = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: selectedPlan.priceId,
          planCode: selectedPlan.planCode,
          mode: 'payment',
          successUrl: `${window.location.origin}${basePath}/submit/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}${basePath}/submit?plan=${selectedPlan.planCode}&mode=purchase`,
          submitId: submitData.submitId,
        }),
      });
      const checkoutContentType = checkoutResponse.headers.get('content-type') || '';
      const checkoutData = checkoutContentType.includes('application/json')
        ? await checkoutResponse.json()
        : {
            error: checkoutResponse.status >= 500
              ? t('errors.stripeUnavailable')
              : t('errors.checkoutStart'),
          };
      if (!checkoutResponse.ok || !checkoutData.url) {
        throw new Error(checkoutResponse.status >= 500
          ? t('errors.stripeUnavailable')
          : t('errors.checkoutStart'));
      }

      sessionStorage.removeItem(PENDING_SUBMISSION_KEY);
      setCheckoutStage('redirecting');
      redirectStarted = true;
      window.location.assign(checkoutData.url);
    } catch (error: any) {
      redirectStarted = false;
      toast.error(error?.message || t('errors.checkoutFailed'));
    } finally {
      if (!redirectStarted) setLoading(false);
    }
  };

  const currentCheckoutStageIndex = checkoutStages.findIndex((stage) => stage.id === checkoutStage);
  const checkoutCopy = checkoutStageCopy[checkoutStage];

  return (
    <>
      <div ref={submitContentRef} className="grid gap-8 lg:grid-cols-[minmax(0,1.25fr)_minmax(300px,.75fr)] lg:items-start">
      <div className="rounded-2xl border border-[color:var(--editorial-line)] bg-white p-5 shadow-[0_18px_50px_rgba(16,35,63,.08)] sm:p-8">
        <div className="border-b border-[color:var(--editorial-line)] pb-7">
          <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--editorial-accent-dark)]">{t('scopeEyebrow')}</p>
              <h2 className="font-editorial mt-2 text-3xl font-semibold tracking-[-0.025em] text-[color:var(--editorial-ink)]">{t('scopeTitle')}</h2>
            </div>
            {drValue && (
              <div className="border-l-2 border-[color:var(--editorial-accent)] pl-3 text-sm text-[color:var(--editorial-ink)]">
                <span className="price-figures font-semibold">DR {drValue}</span>
                <a
                  href="https://ahrefs.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-xs text-[color:var(--editorial-muted)] underline decoration-[color:var(--editorial-line)] underline-offset-4 hover:text-[color:var(--editorial-ink)]"
                >
                  {t('ahrefsLabel')}
                </a>
              </div>
            )}
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label={t('packageGroupLabel')}>
            {plans.map((plan) => {
              const selected = plan.planCode === selectedPlan.planCode;
              return (
                <button
                  key={plan.planCode}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => choosePlan(plan.planCode)}
                  className={cn(
                    'editorial-button min-h-[96px] border p-4 text-left',
                    selected
                      ? 'border-[color:var(--editorial-accent)] bg-[color:var(--editorial-accent-soft)] shadow-[inset_0_0_0_1px_var(--editorial-accent)]'
                      : 'border-[color:var(--editorial-line)] bg-[color:var(--editorial-paper)]'
                  )}
                >
                  <span className="flex items-start justify-between gap-4">
                    <span>
                      <span className="block text-sm font-semibold text-[color:var(--editorial-ink)]">{planCopy(plan, 'shortName')}</span>
                      {plan.originalPrice && plan.originalPrice > plan.price && (
                        <span className="price-figures mt-1 block text-xs text-[color:var(--editorial-muted)] line-through decoration-[color:var(--editorial-accent)]">
                          {formatPrice(plan.originalPrice)}
                        </span>
                      )}
                      <span className="price-figures mt-1 block text-2xl font-semibold text-[color:var(--editorial-ink)]">{formatPrice(plan.price)}</span>
                    </span>
                    <span className={cn('mt-1 flex h-5 w-5 items-center justify-center rounded-full border', selected ? 'border-[color:var(--editorial-accent)] bg-[color:var(--editorial-accent)] text-white' : 'border-slate-300')}>
                      {selected && <Check className="h-3 w-3" aria-hidden="true" />}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <Form {...form}>
          <form
            className="mt-7 space-y-6"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSubmitAttempt();
            }}
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('toolName')}</FormLabel>
                    <FormControl>
                      <Input className="editorial-field px-3" placeholder={t('toolNamePlaceholder')} autoComplete="organization" {...field} />
                    </FormControl>
                    <FormMessage className="text-xs text-red-700" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('category')}</FormLabel>
                    <FormControl>
                      <select className="editorial-field w-full px-3 text-sm" {...field} disabled={isCategoryLoading}>
                        <option value="">{isCategoryLoading ? t('categoryLoading') : t('categorySelect')}</option>
                        {categoryList.map((category) => (
                          <option key={category.id} value={category.category_id}>{category.category_name}</option>
                        ))}
                      </select>
                    </FormControl>
                    <FormDescription className="text-xs leading-5 text-[color:var(--editorial-muted)]">
                      {t('categoryHelp')}
                    </FormDescription>
                    <FormMessage className="text-xs text-red-700" />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('productUrl')}</FormLabel>
                  <FormControl>
                    <Input className="editorial-field px-3" type="url" inputMode="url" placeholder={t('productUrlPlaceholder')} autoComplete="url" {...field} />
                  </FormControl>
                  <FormMessage className="text-xs text-red-700" />
                </FormItem>
              )}
            />

            <section className="rounded-xl border border-[color:var(--editorial-line)] bg-[color:var(--editorial-paper)] p-4 sm:p-5" aria-labelledby="editorial-input-heading">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--editorial-accent-dark)]">{t('editorialEyebrow')}</p>
                <h3 id="editorial-input-heading" className="mt-1 text-lg font-semibold text-[color:var(--editorial-ink)]">{t('editorialTitle')}</h3>
                <p className="mt-1 text-sm leading-6 text-[color:var(--editorial-muted)]">
                  {t('editorialDescription')}
                </p>
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="productBrief"
                  render={({ field }) => (
                    <FormItem className={screenshotUploadEnabled ? '' : 'sm:col-span-2'}>
                      <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('productBrief')} <span className="font-normal text-[color:var(--editorial-muted)]">{t('optional')}</span></FormLabel>
                      <FormControl>
                        <textarea
                          className="editorial-field min-h-[150px] w-full resize-y px-3 py-2 text-sm"
                          placeholder={t('productBriefPlaceholder')}
                          maxLength={2000}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-xs leading-5 text-[color:var(--editorial-muted)]">{t('productBriefHelp')}</FormDescription>
                      <FormMessage className="text-xs text-red-700" />
                    </FormItem>
                  )}
                />

                {screenshotUploadEnabled && (
                  <div>
                    <label className="text-sm font-semibold text-[color:var(--editorial-ink)]" htmlFor="product-screenshot">{t('productScreenshot')} <span className="font-normal text-[color:var(--editorial-muted)]">{t('optional')}</span></label>
                    <div className="mt-2 overflow-hidden rounded-lg border border-[color:var(--editorial-line)] bg-white">
                      {screenshotPreview ? (
                        <div className="relative aspect-[16/10] bg-slate-100">
                          {/* The source is an in-memory object URL selected by the user. */}
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={screenshotPreview} alt={t('screenshotPreviewAlt')} className="h-full w-full object-contain" />
                          <button
                            type="button"
                            onClick={() => { setScreenshotFile(null); setScreenshotUploadError(''); }}
                            className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 shadow-sm"
                            aria-label={t('removeScreenshot')}
                          >
                            <X className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                      ) : (
                        <label htmlFor="product-screenshot" className="flex min-h-[150px] cursor-pointer flex-col items-center justify-center gap-2 p-5 text-center">
                          <ImageIcon className="h-6 w-6 text-[color:var(--editorial-accent)]" aria-hidden="true" />
                          <span className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('chooseScreenshot')}</span>
                          <span className="text-xs leading-5 text-[color:var(--editorial-muted)]">{t('screenshotHelp')}</span>
                        </label>
                      )}
                      <input
                        id="product-screenshot"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="sr-only"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          if (!file) return;
                          const error = validateScreenshotFile(file);
                          if (error) {
                            setScreenshotFile(null);
                            const localizedError = error.includes('PNG')
                              ? t('errors.screenshotType')
                              : error.includes('empty')
                                ? t('errors.screenshotEmpty')
                                : t('errors.screenshotSize');
                            setScreenshotUploadError(localizedError);
                            event.target.value = '';
                            return;
                          }
                          setScreenshotFile(file);
                          setScreenshotUploadError('');
                        }}
                      />
                    </div>
                    {screenshotUploadError && (
                      <div role="alert" className="mt-2 flex items-start justify-between gap-3 text-xs leading-5 text-red-700">
                        <span>{screenshotUploadError} {t('screenshotErrorAction')}</span>
                        <button type="button" className="shrink-0 font-semibold underline underline-offset-2" onClick={() => { setScreenshotFile(null); setScreenshotUploadError(''); }}>{t('remove')}</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-xl border border-[color:var(--editorial-line)] bg-[color:var(--editorial-paper)] p-4 sm:p-5" aria-labelledby="listing-context-heading">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--editorial-accent-dark)]">{t('pricingEyebrow')}</p>
                <h3 id="listing-context-heading" className="mt-1 text-lg font-semibold text-[color:var(--editorial-ink)]">{t('pricingTitle')}</h3>
                <p className="mt-1 text-sm leading-6 text-[color:var(--editorial-muted)]">
                  {t('pricingDescription')}
                </p>
              </div>

              <div className="mt-5 max-w-2xl">
                <FormField
                  control={form.control}
                  name="pricingType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('pricing')} <span aria-hidden="true" className="text-red-700">*</span></FormLabel>
                      <FormControl>
                        <select
                          className="editorial-field w-full px-3 text-sm"
                          required
                          aria-required="true"
                          {...field}
                          onChange={(event) => {
                            const value = event.target.value;
                            field.onChange(value);
                            if (!['free_trial', 'freemium', 'paid'].includes(value)) {
                              form.setValue('billingModel', '');
                              form.setValue('billingPeriod', '');
                              form.setValue('priceBasis', '');
                              form.setValue('priceUnit', '');
                            }
                          }}
                        >
                          <option value="">{t('pricingSelect')}</option>
                          {PRICING_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{t(`pricingOptions.${option.value}`)}</option>
                          ))}
                        </select>
                      </FormControl>
                      <FormDescription className="text-xs leading-5 text-[color:var(--editorial-muted)]">
                        {t('pricingHelp')}
                      </FormDescription>
                      <FormMessage className="text-xs text-red-700" />
                    </FormItem>
                  )}
                />

                {showPaidPricingFields && (
                  <FormField
                    control={form.control}
                    name="billingModel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('chargingModel')} <span aria-hidden="true" className="text-red-700">*</span></FormLabel>
                        <FormControl>
                          <select
                            className="editorial-field w-full px-3 text-sm"
                            required
                            aria-required="true"
                            {...field}
                            onChange={(event) => {
                              const value = event.target.value;
                              field.onChange(value);
                              if (value !== 'subscription') form.setValue('billingPeriod', '');
                              if (!['usage_based', 'credits', 'other'].includes(value)) {
                                form.setValue('priceBasis', '');
                                form.setValue('priceUnit', '');
                              }
                            }}
                          >
                            <option value="">{t('chargingModelSelect')}</option>
                            {BILLING_MODEL_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{t(`billingModels.${option.value}`)}</option>
                            ))}
                          </select>
                        </FormControl>
                        <FormDescription className="text-xs leading-5 text-[color:var(--editorial-muted)]">
                          {t('chargingModelHelp')}
                        </FormDescription>
                        <FormMessage className="text-xs text-red-700" />
                      </FormItem>
                    )}
                  />
                )}

                {showBillingPeriod && (
                  <FormField
                    control={form.control}
                    name="billingPeriod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('billingPeriod')} <span aria-hidden="true" className="text-red-700">*</span></FormLabel>
                        <FormControl>
                          <select className="editorial-field w-full px-3 text-sm" required aria-required="true" {...field}>
                            <option value="">{t('billingPeriodSelect')}</option>
                            {BILLING_PERIOD_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{t(`billingPeriods.${option.value}`)}</option>
                            ))}
                          </select>
                        </FormControl>
                        <FormDescription className="text-xs leading-5 text-[color:var(--editorial-muted)]">
                          {t('billingPeriodHelp')}
                        </FormDescription>
                        <FormMessage className="text-xs text-red-700" />
                      </FormItem>
                    )}
                  />
                )}

                {showPriceBasis && (
                  <FormField
                    control={form.control}
                    name="priceBasis"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('amountType')} <span aria-hidden="true" className="text-red-700">*</span></FormLabel>
                        <FormControl>
                          <select className="editorial-field w-full px-3 text-sm" required aria-required="true" {...field}>
                            <option value="">{t('amountTypeSelect')}</option>
                            {PRICE_BASIS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{t(`priceBasis.${option.value}`)}</option>
                            ))}
                          </select>
                        </FormControl>
                        <FormDescription className="text-xs leading-5 text-[color:var(--editorial-muted)]">
                          {t('amountTypeHelp')}
                        </FormDescription>
                        <FormMessage className="text-xs text-red-700" />
                      </FormItem>
                    )}
                  />
                )}

                {showPriceUnit && (
                  <FormField
                    control={form.control}
                    name="priceUnit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('billingUnit')} <span aria-hidden="true" className="text-red-700">*</span></FormLabel>
                        <FormControl>
                          <Input className="editorial-field px-3" placeholder={t('billingUnitPlaceholder')} required aria-required="true" {...field} />
                        </FormControl>
                        <FormMessage className="text-xs text-red-700" />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {showPaidPricingFields && (
                <div className="mt-5 grid gap-5 sm:grid-cols-[1fr_160px]">
                  <FormField
                    control={form.control}
                    name="startingPrice"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('startingPrice')} {requireStartingPrice ? <span aria-hidden="true" className="text-red-700">*</span> : <span className="font-normal text-[color:var(--editorial-muted)]">{t('optional')}</span>}</FormLabel>
                        <FormControl>
                          <Input className="editorial-field px-3" inputMode="decimal" placeholder="19.00" required={requireStartingPrice} aria-required={requireStartingPrice} {...field} />
                        </FormControl>
                        <FormMessage className="text-xs text-red-700" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="priceCurrency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('currency')} {requireStartingPrice && <span aria-hidden="true" className="text-red-700">*</span>}</FormLabel>
                        <FormControl>
                          <Input
                            className="editorial-field px-3 uppercase"
                            list="submit-price-currencies"
                            maxLength={3}
                            pattern="[A-Za-z]{3}"
                            placeholder="USD"
                            required={requireStartingPrice}
                            aria-required={requireStartingPrice}
                            autoCapitalize="characters"
                            spellCheck={false}
                            {...field}
                            onChange={(event) => field.onChange(event.target.value.toUpperCase())}
                          />
                        </FormControl>
                        <datalist id="submit-price-currencies">
                          {PRICE_CURRENCY_OPTIONS.map((currency) => (
                            <option key={currency} value={currency} />
                          ))}
                        </datalist>
                        <FormDescription className="text-xs leading-5 text-[color:var(--editorial-muted)]">
                          {t('currencyHelp')}
                        </FormDescription>
                        <FormMessage className="text-xs text-red-700" />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="suggestedKeywords"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-semibold text-[color:var(--editorial-ink)]">{t('suggestedKeywords')}</FormLabel>
                      <FormControl>
                        <textarea
                          className="editorial-field min-h-[108px] w-full resize-y px-3 py-2 text-sm"
                          placeholder={t('suggestedKeywordsPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-xs leading-5 text-[color:var(--editorial-muted)]">
                        {t('suggestedKeywordsHelp')}
                      </FormDescription>
                      <FormMessage className="text-xs text-red-700" />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            {selectedPlan.includesVideo && (
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[color:var(--editorial-line)] bg-[color:var(--editorial-paper)] p-4">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm mt-0.5 border-[color:var(--editorial-ink)] [--chkbg:var(--editorial-accent)] [--chkfg:white]"
                  checked={videoAgreement}
                  onChange={(event) => setVideoAgreement(event.target.checked)}
                />
                <span className="text-sm leading-6 text-slate-700">
                  {t('videoAgreement')}
                </span>
              </label>
            )}

            {loginRequired && (
              <div role="alert" className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-slate-700">
                <p className="font-semibold text-[color:var(--editorial-ink)]">{t('signInTitle')}</p>
                <p className="mt-1 leading-6">{t('signInDescription')}</p>
                <button
                  type="button"
                  onClick={() => router.push(getSignInDestination())}
                  className="editorial-button mt-3 min-h-10 border border-blue-700 bg-white px-4 font-semibold text-blue-800"
                >
                  {t('signInButton')}
                </button>
              </div>
            )}

            <button type="submit" disabled={loading || authChecking} className="editorial-button editorial-button-primary flex min-h-12 w-full items-center justify-center gap-2 px-6 text-sm font-semibold" aria-busy={loading || authChecking}>
              {authChecking ? (
                <>
                  <LoaderCircle className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  {t('checkingAccount')}
                </>
              ) : loading ? (
                <>
                  <LoaderCircle className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  {t('preparingCheckout')}
                </>
              ) : (
                <>
                  {t('continuePayment', { price: formatPrice(selectedPlan.price) })}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </>
              )}
            </button>
            <p className="text-center text-xs leading-5 text-[color:var(--editorial-muted)]">
              {selectedPlan.includesVideo
                && selectedPlan.originalPrice
                && selectedPlan.originalPrice > selectedPlan.price
                ? t('videoPriceNote', {
                    price: formatPrice(selectedPlan.price),
                    originalPrice: formatPrice(selectedPlan.originalPrice),
                  })
                : t('listingPriceNote')}
            </p>
          </form>
        </Form>
      </div>

        <aside className="border-t-2 border-[color:var(--editorial-ink)] pt-6 lg:sticky lg:top-8" aria-label={t('selectedPackageSummary')}>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--editorial-muted)]">{t('selectedPackage')}</p>
            <h2 className="font-editorial mt-2 text-3xl font-semibold text-[color:var(--editorial-ink)]">{planCopy(selectedPlan, 'name')}</h2>
          </div>
          {selectedPlan.includesVideo ? <Film className="h-7 w-7 text-[color:var(--editorial-accent)]" aria-hidden="true" /> : <ShieldCheck className="h-7 w-7 text-[color:var(--editorial-accent)]" aria-hidden="true" />}
        </div>
        <p className="mt-4 text-sm leading-6 text-[color:var(--editorial-muted)]">{planCopy(selectedPlan, 'description')}</p>
        {selectedPlan.includesVideo && (
          <section className="mt-6 border-y border-[color:var(--editorial-line)] py-5" aria-labelledby="video-distribution-heading">
            <h3 id="video-distribution-heading" className="text-base font-semibold text-[color:var(--editorial-ink)]">
              {t('videoDistribution')}
            </h3>
            <p className="mt-1 text-sm leading-6 text-[color:var(--editorial-muted)]">
              {t('videoDistributionDescription')}
            </p>
            <Link
              href={localPath(locale, '/videos')}
              className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--editorial-accent-dark)] underline decoration-[color:var(--editorial-accent)] underline-offset-4 transition-colors hover:text-[color:var(--editorial-ink)]"
            >
              {t('viewPublishedVideos')}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>

            <div className="mt-5 divide-y divide-[color:var(--editorial-line)] border-t border-[color:var(--editorial-line)]">
              <div className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--editorial-ink)]">{t('publishingNow')}</p>
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[color:var(--editorial-success)]">{t('included')}</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-3">
                  <a
                    href="https://www.youtube.com/@AIToolly-FindBestAITools"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--editorial-ink)] underline decoration-[color:var(--editorial-line)] underline-offset-4 hover:decoration-[color:var(--editorial-accent)]"
                  >
                    <Youtube className="h-4 w-4" aria-hidden="true" />
                    YouTube
                  </a>
                  <a
                    href="https://x.com/aitoolly"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--editorial-ink)] underline decoration-[color:var(--editorial-line)] underline-offset-4 hover:decoration-[color:var(--editorial-accent)]"
                  >
                    X
                  </a>
                </div>
              </div>

              <div className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--editorial-muted)]">{t('comingNext')}</p>
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[color:var(--editorial-muted)]">{t('roadmap')}</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm font-semibold text-[color:var(--editorial-muted)]">
                  <span className="inline-flex items-center gap-2">
                    <Instagram className="h-4 w-4" aria-hidden="true" />
                    Instagram
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Music2 className="h-4 w-4" aria-hidden="true" />
                    TikTok
                  </span>
                </div>
                <p className="mt-3 text-xs leading-5 text-[color:var(--editorial-muted)]">
                  {t('plannedChannelsNote')}
                </p>
              </div>
            </div>
          </section>
        )}
        <ul className="mt-6 divide-y divide-[color:var(--editorial-line)] border-y border-[color:var(--editorial-line)]">
          {selectedPlan.features.map((feature, index) => (
            <li key={feature.name} className="flex items-start gap-3 py-3 text-sm leading-6 text-slate-700">
              <Check className="mt-1 h-4 w-4 shrink-0 text-[color:var(--editorial-success)]" aria-hidden="true" />
              {planFeature(selectedPlan, index)}
            </li>
          ))}
        </ul>
        <div className="mt-6 flex items-end justify-between gap-4">
          <span className="text-sm text-[color:var(--editorial-muted)]">{t('deliveryTarget')}</span>
          <span className="price-figures text-sm font-semibold text-[color:var(--editorial-ink)]">{t('deliveryBusinessDays', { count: selectedPlan.deliveryDays })}</span>
        </div>
        </aside>
      </div>

      {loading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 px-4 py-8 backdrop-blur-[2px]">
          <div
            ref={checkoutDialogRef}
            role="dialog"
            aria-modal="true"
            aria-busy="true"
            aria-labelledby="checkout-waiting-title"
            aria-describedby="checkout-waiting-description"
            tabIndex={-1}
            className="relative w-full max-w-[460px] overflow-hidden rounded-2xl border border-blue-100 bg-white shadow-[0_30px_90px_rgba(16,35,63,.34)] outline-none"
          >
            <div className="h-1 bg-[color:var(--editorial-line)]" aria-hidden="true">
              <div
                className="h-full bg-[color:var(--editorial-accent)] transition-[width] duration-300 ease-out motion-reduce:transition-none"
                style={{ width: `${((currentCheckoutStageIndex + 1) / checkoutStages.length) * 100}%` }}
              />
            </div>

            <div className="p-6 sm:p-8" aria-live="polite">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-[color:var(--editorial-accent)]">
                  {checkoutStage === 'redirecting' ? (
                    <CreditCard className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <LoaderCircle className="h-5 w-5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  )}
                </div>
                <div className="min-w-0 pt-0.5">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--editorial-accent-dark)]">{checkoutCopy.eyebrow}</p>
                  <h2 id="checkout-waiting-title" className="font-editorial mb-0 mt-1.5 text-2xl font-semibold tracking-[-0.02em] text-[color:var(--editorial-ink)] sm:text-[1.75rem]">
                    {checkoutCopy.title}
                  </h2>
                  <p id="checkout-waiting-description" className="mt-2 text-sm leading-6 text-[color:var(--editorial-muted)]">
                    {checkoutCopy.description}
                  </p>
                </div>
              </div>

              <ol className="mt-7 grid grid-cols-3 gap-2" aria-label={t('checkoutProgress')}>
                {checkoutStages.map((stage, index) => {
                  const isComplete = index < currentCheckoutStageIndex;
                  const isCurrent = index === currentCheckoutStageIndex;

                  return (
                    <li
                      key={stage.id}
                      aria-current={isCurrent ? 'step' : undefined}
                      className={cn(
                        'border-t-2 pt-3',
                        index <= currentCheckoutStageIndex ? 'border-[color:var(--editorial-accent)]' : 'border-[color:var(--editorial-line)]'
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-bold',
                          isComplete && 'border-[color:var(--editorial-accent)] bg-[color:var(--editorial-accent)] text-white',
                          isCurrent && 'border-[color:var(--editorial-accent)] bg-blue-50 text-[color:var(--editorial-accent-dark)]',
                          !isComplete && !isCurrent && 'border-slate-300 bg-white text-slate-500'
                        )}
                      >
                        {isComplete ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : index + 1}
                      </span>
                      <span className={cn('mt-2 block text-[11px] font-semibold leading-4 sm:text-xs', index <= currentCheckoutStageIndex ? 'text-[color:var(--editorial-ink)]' : 'text-slate-500')}>
                        {stage.label}
                      </span>
                    </li>
                  );
                })}
              </ol>

              <div className="mt-7 flex items-center justify-between gap-5 border-y border-[color:var(--editorial-line)] py-4">
                <div className="min-w-0">
                  <p className="text-xs text-[color:var(--editorial-muted)]">{t('selectedPackage')}</p>
                  <p className="mt-1 truncate text-sm font-semibold text-[color:var(--editorial-ink)]">{planCopy(selectedPlan, 'name')}</p>
                </div>
                <p className="price-figures shrink-0 text-xl font-semibold text-[color:var(--editorial-ink)]">{formatPrice(selectedPlan.price)}</p>
              </div>

              <p className="mt-5 flex items-center justify-center gap-2 text-center text-xs leading-5 text-[color:var(--editorial-muted)]">
                <LockKeyhole className="h-3.5 w-3.5 shrink-0 text-[color:var(--editorial-accent-dark)]" aria-hidden="true" />
                {t('keepTabOpen')}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
